import { create } from "zustand";
import { cryptoEngine } from "../crypto/index.js";
import { api } from "../lib/api.js";
import { keyvault } from "../storage/keyvault.js";
import { clearAllConversations, getMeta, putMeta, wipeDatabase } from "../storage/db.js";
import { startSweeper, stopSweeper } from "../storage/sweeper.js";
import { loadAccount, messaging, type Account } from "../services/messaging.js";
import { setBadgeCount } from "../badge.js";
import { disableNotifications, syncNotifications } from "../notifications.js";
import { startLockWatch, stopLockWatch } from "../services/lockWatch.js";
import { callInProgress, useCallStore } from "./call.js";

export type Phase = "loading" | "onboarding" | "locked" | "ready";

/** How long the app can be away before a fresh boot wipes chat/call history. */
const AWAY_WIPE_THRESHOLD_MS = 2 * 60 * 1000;

interface SessionState {
  phase: Phase;
  account: Pick<Account, "userId" | "username"> | null;
  channelStatus: string;
  error: string | null;

  init: () => Promise<void>;
  register: (username: string, passphrase: string, registrationCode?: string) => Promise<void>;
  unlockWithPassphrase: (passphrase: string) => Promise<boolean>;
  unlockWithPasskey: () => Promise<boolean>;
  lock: () => void;
  signOut: () => Promise<void>;
}

async function enterReady(set: (s: Partial<SessionState>) => void): Promise<void> {
  const acc = await loadAccount();
  if (!acc) {
    set({ phase: "onboarding" });
    return;
  }
  const snapshot = await getMeta<string>("cryptoSnapshot");
  if (snapshot && !cryptoEngine.ready) await cryptoEngine.restore(snapshot);
  stopLockWatch();
  await messaging.start(acc);
  void syncNotifications(acc);
  startSweeper();
  set({ phase: "ready", account: { userId: acc.userId, username: acc.username } });
}

export const useSession = create<SessionState>((set) => ({
  phase: "loading",
  account: null,
  channelStatus: "idle",
  error: null,

  async init() {
    messaging.onStatus((s) => set({ channelStatus: s }));

    // Away long enough that this counts as "left," not "just refreshed" —
    // wipe chat/call history. Doesn't need the vault unlocked: deleting rows
    // doesn't require decrypting them.
    const gapMs = await keyvault.takeHiddenGapMs();
    if (gapMs !== null && gapMs > AWAY_WIPE_THRESHOLD_MS) {
      await clearAllConversations().catch(() => undefined);
      await setBadgeCount(0).catch(() => undefined);
    }

    if (!(await keyvault.hasVault())) {
      set({ phase: "onboarding" });
      return;
    }
    set({ phase: "locked" });
  },

  async register(username, passphrase, registrationCode) {
    set({ error: null });
    try {
      await keyvault.enrollPassphrase(passphrase);
      const identity = await cryptoEngine.createIdentity();
      const res = await api.register({
        username,
        mlsCredential: identity.mlsCredential,
        signaturePublicKey: identity.signaturePublicKey,
        keyPackages: identity.keyPackages,
        ...(registrationCode !== undefined ? { registrationCode } : {}),
      });
      cryptoEngine.bindUserId(res.userId);
      await putMeta("account", { userId: res.userId, username: res.username, token: res.token });
      await putMeta("cryptoSnapshot", await cryptoEngine.snapshot());
      await enterReady(set);
    } catch (err) {
      await keyvault.wipe().catch(() => undefined);
      set({ error: err instanceof Error ? err.message : "Registration failed." });
      throw err;
    }
  },

  async unlockWithPassphrase(passphrase) {
    set({ error: null });
    const ok = await keyvault.unlockPassphrase(passphrase);
    if (!ok) {
      set({ error: "Wrong passphrase." });
      return false;
    }
    await enterReady(set);
    return true;
  },

  async unlockWithPasskey() {
    set({ error: null });
    const ok = await keyvault.unlockPasskey();
    if (!ok) {
      set({ error: "Passkey unlock failed." });
      return false;
    }
    await enterReady(set);
    return true;
  },

  lock() {
    // Hang up first, while the channel is still live, so the peer actually
    // gets the "end" — otherwise the call state survives the lock.
    if (callInProgress()) useCallStore.getState().hangUp();
    stopSweeper();
    const acc = messaging.account;
    messaging.stop();
    keyvault.lock();
    set({ phase: "locked", account: null });
    // Keep ringing / chiming on the lock screen. Only the relay login stays in
    // memory for this — never the key that decrypts anything.
    if (acc) startLockWatch({ userId: acc.userId, token: acc.token });
  },

  async signOut() {
    stopLockWatch();
    stopSweeper();
    const acc = messaging.account;
    if (acc) await disableNotifications(acc);
    messaging.stop();
    await wipeDatabase().catch(() => undefined);
    await keyvault.wipe().catch(() => undefined);
    await setBadgeCount(0).catch(() => undefined);
    set({ phase: "onboarding", account: null });
  },
}));
